/**
 * Guards de rôle — Empêche un utilisateur d'accéder à une zone qui n'est pas pour lui.
 *
 * Règles :
 * - Un agent terrain ne doit PAS accéder aux dashboards/rapports/gouvernance/formation/audit
 *   ni aux modules financiers (subscription, carbon-premiums, distributions, lots...).
 * - Un agent peut co-remplir : /cooperative/pdc-v2, /cooperative/membres/adhesion,
 *   /cooperative/members/:id/parcels, /cooperative/parcels/new (page d'orientation),
 *   /cooperative/outils-terrain.
 * - Non authentifié → redirection login.
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const AGENT_ROLES = ['field_agent', 'agent_terrain'];
const FARMER_ROLES = ['farmer', 'planteur'];

// Redirection par défaut selon le rôle
const dashboardForRole = (userType) => {
  if (AGENT_ROLES.includes(userType)) return '/agent/terrain';
  if (FARMER_ROLES.includes(userType)) return '/farmer/dashboard';
  if (userType === 'exporter') return '/exporter/dashboard';
  if (userType === 'auditor') return '/auditor/dashboard';
  if (userType === 'admin' || userType === 'super_admin') return '/admin/dashboard';
  return '/';
};

/**
 * RequireRole : autorise UNIQUEMENT les rôles listés.
 * Redirige les autres vers leur dashboard naturel (ou /login si non authentifié).
 */
export const RequireRole = ({ allowed = [], children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  const ut = user?.user_type;
  if (!allowed.includes(ut)) {
    return <Navigate to={dashboardForRole(ut)} replace />;
  }
  return children;
};

/**
 * CoopOnlyRoute : réservée aux gestionnaires de coopérative + admin.
 * Bloque agents, planteurs, exportateurs, auditeurs.
 */
export const CoopOnlyRoute = ({ children }) => (
  <RequireRole allowed={['cooperative', 'admin', 'super_admin']}>{children}</RequireRole>
);

/**
 * CoopOrAgentRoute : coop + agent terrain (collecte partagée).
 * Utilisée pour PDC, adhésion, parcelles (lecture), outils terrain.
 */
export const CoopOrAgentRoute = ({ children }) => (
  <RequireRole allowed={['cooperative', 'admin', 'super_admin', 'field_agent', 'agent_terrain']}>{children}</RequireRole>
);

/**
 * BlockAgentFromCoopDashboard : version legacy, conservée pour rétrocompatibilité.
 * Préférer CoopOnlyRoute pour les nouvelles routes.
 */
export const BlockAgentFromCoopDashboard = ({ children }) => (
  <CoopOnlyRoute>{children}</CoopOnlyRoute>
);

export default RequireRole;
